import { useState } from 'react';
import { toast } from 'react-toastify';
import { loadRazorpay } from '../utils/loadRazorpay';
import { proceedToCheckout, syncSubscription } from '../services/subscription';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearCheckout } from '../store/reducers/checkoutSlice/checkoutSlice';
import { fetchUserInfo } from '../store/action/userInfo';

export const usePayment = () => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const initiatePayment = async ({
		billingDetails,
		plan,
		paymentType,
		billingType,
		paymentMethod,
	}) => {
		try {
			setLoading(true);

			const selectedMethod = paymentMethod;
			const isRecurring = paymentType === 'recurring';
			const activeBilling = plan?.billing?.find((item) => item.period === billingType);

			if (!activeBilling) {
				toast.error('Invalid billing plan');
				return;
			}

			const payload = {
				billing: {
					company_name: billingDetails.name,
					phone: billingDetails.phone,
					address_line1: billingDetails.address,
					city: billingDetails.city,
					state: billingDetails.state,
					pincode: billingDetails.pincode,
					country: billingDetails.country,
					gstin_required: billingDetails.gst,
					gstin: billingDetails.gstin || null,
				},
				plan: {
					razorpay_plan_id: activeBilling?.razorpay_plan_id,
					billing_period: activeBilling?.period,
					amount: activeBilling?.amount_in_rupees,
					is_recurring: isRecurring,
					payment_method: selectedMethod,
				},
			};

			const response = await proceedToCheckout(payload);

			if (!response?.success) {
				toast.error(response?.message || 'Unable to proceed checkout');
				return;
			}

			const razorpayLoaded = await loadRazorpay();

			if (!razorpayLoaded) {
				toast.error('Razorpay SDK failed to load');
				return;
			}

			const subscription = response?.data?.subscription;
			const options = {
				key: subscription?.razorpay_key_id,
				subscription_id: subscription?.subscription_id,
				name: 'Pharmnex',
				description: `${subscription?.plan_name} - ${subscription?.billing_period}`,
				currency: subscription?.currency || 'INR',

				prefill: {
					name: billingDetails?.name,
					contact: billingDetails?.phone,
					email: subscription?.customer?.email || '',
				},

				method: {
					upi: selectedMethod === 'upi',
					card: selectedMethod === 'card',
					netbanking: false,
					wallet: false,
					emi: false,
					paylater: false,
				},

				// `preferences.show_default_blocks: false` used to be set here with
				// custom upi/card blocks. Razorpay lists a customer's SAVED CARDS in
				// its default blocks, so suppressing them meant the user had to
				// re-enter their card on every payment — and Razorpay issued a brand
				// new token each time, which surfaced as a duplicate entry under
				// Manage Billing.
				//
				// The `method` map above already restricts checkout to the single
				// method the user picked, so the custom blocks were only relabelling
				// what `method` had already narrowed. Leaving the default blocks in
				// place lets an existing saved card be selected and reused, and a new
				// card is tokenised only when the user actually enters a different
				// one.
				config: {
					display: {
						preferences: { show_default_blocks: true },
					},
				},

				...(isRecurring && { recurring: 1 }),

				theme: { color: '#3B58FF' },

				handler: async (paymentResponse) => {
					dispatch(clearCheckout());

					// Activation used to depend entirely on Razorpay's webhook, which
					// lands seconds-to-minutes after the user finishes paying. Fetching
					// user info straight away therefore returned the OLD plan (or
					// "Payment Pending"), and the user had to wait and refresh.
					//
					// Confirm the payment against Razorpay first so the backend applies
					// the new status synchronously; only then refresh Redux.
					try {
						await syncSubscription({
							razorpay_subscription_id:
								paymentResponse?.razorpay_subscription_id || subscription?.subscription_id,
						});
					} catch (err) {
						// Non-fatal: the webhook remains the backstop, and
						// /payment-success polls for the settled status.
						console.error('Failed to confirm subscription after payment:', err);
					}

					// openSubscriptionCheckout's success path does a full page
					// reload, which incidentally refreshes the app's Redux
					// userInfo/subscription state. This path navigates via
					// React Router instead (no reload), so without this the
					// rest of the app (sidebar, plan gates, etc.) would keep
					// showing pre-payment subscription state until the user
					// manually refreshed. Await it before navigating so
					// /payment-success and everything after it sees the new plan.
					try {
						await dispatch(fetchUserInfo());
					} catch (err) {
						console.error('Failed to refresh user info after payment:', err);
					}
					navigate('/payment-success', { replace: true });
				},

				modal: {
					ondismiss: () => {
						toast.error('Payment cancelled');
					},
				},
			};

			const razorpay = new window.Razorpay(options);

			razorpay.on('payment.failed', (response) => {
				toast.error(response?.error?.description || 'Payment failed');
			});

			razorpay.open();
		} catch (error) {
			console.error(error);
			toast.error(error?.response?.data?.message || 'Payment initialization failed');
		} finally {
			setLoading(false);
		}
	};

	const openSubscriptionCheckout = async ({
		subscription,
		billingDetails,
		paymentMethod = 'card',
	}) => {
		try {
			setLoading(true);

			const razorpayLoaded = await loadRazorpay();

			if (!razorpayLoaded) {
				toast.error('Razorpay SDK failed to load');
				return;
			}

			const options = {
				key: subscription.razorpay_key_id,
				subscription_id: subscription.subscription_id,
				name: 'Pharmnex',
				description: `${subscription.plan_name} - ${subscription.billing_period}`,
				currency: subscription.currency || 'INR',

				prefill: {
					name: billingDetails?.company_name,
					contact: billingDetails?.phone,
					email: subscription.customer?.email,
				},

				// Methods omitted from this map fall back to whatever is enabled on
				// the Razorpay account, so listing only upi/card left netbanking,
				// wallet, emi and paylater available here — while the other checkout
				// path in this file disables them explicitly. netbanking is how an
				// eMandate subscription gets authorised, and Razorpay refuses to
				// update the plan on an eMandate subscription
				// ("subscriptions cannot be updated when payment mode is emandate"),
				// which is what scheduled downgrades depend on. Disabling them here
				// keeps both paths consistent and keeps every subscription on a
				// method that can actually be downgraded later.
				method: {
					upi: paymentMethod === 'upi',
					card: paymentMethod === 'card',
					netbanking: false,
					wallet: false,
					emi: false,
					paylater: false,
				},

				recurring: 1,

				theme: {
					color: '#3B58FF',
				},

				handler: async (paymentResponse) => {
					// Same race as the checkout path: reloading immediately would
					// re-read a subscription the webhook hasn't activated yet, so the
					// page would come back showing the previous plan. Confirm against
					// Razorpay first, then reload.
					try {
						await syncSubscription({
							razorpay_subscription_id:
								paymentResponse?.razorpay_subscription_id || subscription.subscription_id,
						});
					} catch (err) {
						console.error('Failed to confirm subscription after upgrade payment:', err);
					}
					window.location.reload();
				},
			};

			new window.Razorpay(options).open();
		} catch (error) {
			console.error('Failed to open Razorpay subscription checkout:', error);
			toast.error('Payment window failed to open. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return {
		initiatePayment,
		openSubscriptionCheckout,
		loading,
	};
};
